/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/


import {EventEmitter, Injectable} from '@angular/core';
import {WebEamNextConfigModel} from '../utils/web-eam-next-config.model';
import {Observable, ReplaySubject} from 'rxjs';
import {AlertNotification} from '../../alert.notification.model';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModalComponent} from '../confirm-modal/deprecated-modals/confirm-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModal} from '../confirm-modal/deprecated-modals/confirm-modal.model';
import {Router} from '@angular/router';
import {SpoSearchCriteriaService} from '../../spo/services/spo-search-criteria.service';
import {ClaimSearchCriteriaService} from '../../claim/services/claim-search-criteria.service';
import {PartNumberChangesSearchCriteriaService} from '../../part-number-changes/services/part-number-changes-search-criteria.service';
/** Token data acquired from the WebEAM (Alpha SSO) implicit flow */
export interface AuthToken {
    access_token: string;
    id_token: string;
    issued: string | number;
    expires_in: string | number;
    token_type: string;
    refresh_token: string;
    user_context: any;
    state: string;
    scope: string[] | null;
    nonce?: string;
}

const NONCE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const NONCE_LENGTH = 15;
const OAUTH_RESPONSE_PARAMS = [
    'access_token', 'id_token', 'expires_in', 'token_type', 'refresh_token', 'state', 'scope', 'nonce', 'error', 'error_description'
];

@Injectable({
    providedIn: 'root'
})

/**
 * User authentication class for the ORCAN FrontEnd
 */
export class AuthenticationService {
    /** User authorization header*/
    public userAuthorizationHeader = '';
    private authenticationSubject = new ReplaySubject<void>(1);
    /** ORCAN authentication settings*/
    private scope = ['openid', 'profile', 'email'];
    private responseType = ['id_token', 'token'];
    private readonly webEamConfig: WebEamNextConfigModel = (window as any).webEamNextConfig as WebEamNextConfigModel;
    private token: AuthToken = AuthenticationService.emptyToken();

    private lastModal: ConfirmModalComponent = null;
    private alertEmitter = new EventEmitter<[string, AlertNotification]>();

    constructor(private modalService: NgbModal,
                private router: Router,
                private spoSearchCriteriaService: SpoSearchCriteriaService,
                private claimSearchCriteriaService: ClaimSearchCriteriaService,
                private partNumberChangesCriteriaService: PartNumberChangesSearchCriteriaService) {
        this.alertEmitter.subscribe(([title, notification]: [string, AlertNotification]) => {
            if (this.lastModal) {
                this.lastModal.activeModal.dismiss();
            }

            this.lastModal = this.modalService.open(ConfirmModalComponent, {
                centered: true,
                keyboard: false
            }).componentInstance.initModal({
                titleText: title,
                subtitleText: notification.message,
                alertNotificationSeverity: notification.alertNotificationSeverity,
                primaryButtonText: 'OK',
                primaryButtonCustomizedClass: 'btn-secondary',
                hasCloseButton: false
            } as ConfirmModal).confirmEvent.subscribe(() => {
                // clear all cached filters
                this.spoSearchCriteriaService.clear();
                this.claimSearchCriteriaService.clear();
                this.partNumberChangesCriteriaService.clear();
                // Go to home
                window.location.href = '/';
            });
        });
    }

    get onAuthentication(): Observable<void> {
        return this.authenticationSubject.asObservable();
    }

    /**
     * Initializes the authentication of the user, using the cached token or the WebEAM implicit flow
     */
    initAuthentication(): void {
        const authToken: AuthToken = JSON.parse(localStorage.getItem('AuthToken'));

        if (authToken != null && this.millisUntilExpiration(authToken) > 0) {
            this.token = {
                ...AuthenticationService.emptyToken(),
                access_token: authToken.access_token,
                id_token: authToken.id_token,
                issued: authToken.issued,
                expires_in: authToken.expires_in,
                token_type: authToken.token_type,
                refresh_token: authToken.refresh_token,
                user_context: authToken.user_context,
                state: authToken.state,
                scope: authToken.scope,
                nonce: authToken.nonce
            };
            this.getUserAuthorizationHeader();
            this.expiredTokenHandler(this.millisUntilExpiration(authToken));
        } else {
            this.fetchToken();
        }
    }

    /**
     * Sets the user authorization header for BE requests
     */
    getUserAuthorizationHeader(): void {
        if (this.token) {
            this.userAuthorizationHeader = `Bearer ${this.token.access_token}`;
            this.emitHasAuthentication();
        } else {
            this.userAuthorizationHeader = '';
        }
    }

    /**
     * Calculates the milliseconds until the token expires.
     * @param token the token.
     * @return the token expiration in milliseconds.
     * @throws {TypeError} when <i><code>token.issued</code></i> or <i><code>token.expires_in</code></i> aren't numbers.
     */
    millisUntilExpiration(token: AuthToken): number {
        // token.issued is in milliseconds, token.expires_in is in seconds
        const authTokenExpiration: number = token != null ?
                parseInt(String(token.issued), 10) + parseInt(String(token.expires_in), 10) * 1000 : NaN;

        if (!Number.isInteger(authTokenExpiration)) {
            throw new TypeError(`Token expiration couldn't be calculated, ` +
                    `due to the 'issued' or the 'expires_in' fields not being numbers.`);
        }

        const millisUntilExpiration = authTokenExpiration - Date.now();

        return millisUntilExpiration;
    }

    /**
     * Handles the token expiration.
     * @param millisUntilExpiration the milliseconds until the E2E token expires
     */
    expiredTokenHandler(millisUntilExpiration: number): void {
        setTimeout(() => {
            const alertTitle = 'Session expired';
            const alertNotification: AlertNotification = {
                alertNotificationSeverity: AlertNotificationSeverity.WARNING,
                message: `Your session has expired. Click OK to enter your credentials again.`
            };

            localStorage.removeItem('AuthToken');
            localStorage.removeItem('E2Etoken');

            this.alertEmitter.emit([alertTitle, alertNotification]);
        }, millisUntilExpiration);
    }

    emitHasAuthentication(): void {
        this.authenticationSubject.next();
        this.authenticationSubject.complete();
    }

    private static emptyToken(): AuthToken {
        return {
            access_token: '',
            issued: '',
            expires_in: '',
            token_type: 'Bearer',
            refresh_token: '',
            user_context: null,
            state: '',
            scope: null,
            id_token: ''
        };
    }

    /**
     * Reads the token from the URL hash (redirect from WebEAM) or redirects the user to the WebEAM authorize endpoint.
     */
    private fetchToken(): void {
        const locationStr = window.location.hash;
        const urlParams = locationStr.slice(locationStr.lastIndexOf('#') + 1);

        const data = this.parseUrlParams(urlParams);

        if (data.has('access_token')) {
            this.handleTokenResponse(data);
        } else if (!data.has('error')) {
            // No token and no error (empty hash, router hash or unknown data): authenticate again
            this.redirectToAuthorize();
        }
        // TODO: add authentication error handling for 'error'
    }

    /**
     * Parses the URL hash into a Map, keeping only the known OAuth response parameters.
     * A Map and an allowlist are used so that user-controlled keys never become object property names.
     */
    private parseUrlParams(urlParams: string): Map<string, string> {
        const data = new Map<string, string>();

        urlParams.split('&').forEach((pair: string) => {
            const separator = pair.indexOf('=');
            const rawKey = separator < 0 ? pair : pair.slice(0, separator);
            const rawValue = separator < 0 ? '' : pair.slice(separator + 1);

            try {
                const key = decodeURIComponent(rawKey);

                if (OAUTH_RESPONSE_PARAMS.includes(key)) {
                    data.set(key, decodeURIComponent(rawValue));
                }
            } catch {
                // Ignore malformed parameters
            }
        });

        return data;
    }

    private handleTokenResponse(data: Map<string, string>): void {
        const scope = data.get('scope');

        this.token = {
            ...AuthenticationService.emptyToken(),
            access_token: data.get('access_token'),
            expires_in: data.get('expires_in') || '',
            issued: Date.now(),
            refresh_token: data.get('refresh_token') || '',
            state: data.get('state') || '',
            scope: scope ? scope.split(' ') : null,
            id_token: data.get('id_token') || '',
            nonce: data.get('nonce') || ''
        };

        this.getUserAuthorizationHeader();
        this.expiredTokenHandler(this.millisUntilExpiration(this.token));
        localStorage.setItem('AuthToken', JSON.stringify(this.token));
        this.router.navigateByUrl(this.token.state);

        this.fetchUserContext();
    }

    /**
     * Gets the user context (userinfo) of the token and stores it in the cached token.
     */
    private fetchUserContext(): void {
        const accessToken = this.token.access_token;

        fetch(`${this.oauthBaseUrl()}/userinfo`, {
            method: 'POST',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            body: `access_token=${accessToken}`
        })
                .then((response: Response) => response.ok ? response.json() : null)
                .then((userContext: any) => {
                    if (userContext && this.token.access_token === accessToken) {
                        this.token.user_context = userContext;
                        localStorage.setItem('AuthToken', JSON.stringify(this.token));
                    }
                })
                .catch(() => {
                    // The user context isn't mandatory
                });
    }

    private redirectToAuthorize(): void {
        const params = [
            `?response_type=${this.responseType.join(' ')}`,
            this.webEamConfig.clientId ? `&client_id=${this.webEamConfig.clientId}` : '',
            this.webEamConfig.authenticationRedirectUri ? `&redirect_uri=${this.webEamConfig.authenticationRedirectUri}` : '',
            window.location.pathname ? `&state=${window.location.pathname}` : '',
            `&scope=${this.scope.join(' ')}`,
            `&nonce=${this.generateNonce()}`
        ].join('');

        window.location.replace(encodeURI(`${this.oauthBaseUrl()}/authorize${params}`));
    }

    private oauthBaseUrl(): string {
        return `${this.webEamConfig.authenticationUrl}/oauth2${this.webEamConfig.realmPath}`;
    }

    private generateNonce(): string {
        const random = crypto.getRandomValues(new Uint32Array(NONCE_LENGTH));

        return Array.from(random, (value: number) => NONCE_CHARS.charAt(value % NONCE_CHARS.length)).join('');
    }
}
